// External libraries
import React from 'react';
import Loadable from 'react-loadable';
import { isFunction, isNumber } from '@checkout/react-components';

// Internal modules
import { fallback } from './redirects';
import { $logger, fptiTrackPageLoad } from '../logger';

/**
 * @function getLazyLoaderRenderFunction
 * @param {object} options | { fptiTrackName, getComponentName componentName }
 *  fptiTrackName: used when tracking name for FPTI page load is different
 *    than the component name
 *  getComponentName: an optional function that will return the component name
 *  componentName: the named export that is to be loaded and rendered
 * @returns {func}
 */
const getLazyLoaderRenderFunction = options => {
  const { fptiTrackName, getComponentName, componentName } = options;
  return function render(loaded, props) {
    const exportName = isFunction(getComponentName)
      ? getComponentName()
      : componentName;
    if (!this.isLoaded) {
      this.isLoaded = true;
      fptiTrackPageLoad.handleItemLoaded(fptiTrackName || exportName);
    }

    const Component = loaded[exportName];
    if (!Component) {
      $logger.error('weasley_component_not_found', { component: exportName });
    }

    return <Component {...props} />;
  };
};

/**
 * @function lazyLoader
 * @param {object} props | same as object passed to `Loadable()` plus
 * a `name` prop to log the name of the component on an error
 * @returns {func}
 * @description Returns a react-loadable `Loadable` component
 * that will fallback to xoon if a loading error occurs
 */
const lazyLoader = ({ loader, loading, name, ...rest }) => {
  const options = {};

  if (isFunction(rest.render)) {
    options.render = rest.render;
  }

  if (isNumber(rest.delay)) {
    options.delay = rest.delay;
  }

  if (isNumber(rest.timeout)) {
    options.timeout = rest.timeout;
  }

  return Loadable({
    loader,
    loading: props => {
      if (props.error) {
        fallback({ reason: `${name}_component_failed_to_load` });

        // React wants a component returned, otherwise it freaks out...
        return React.createElement('p');
      }

      return loading(props);
    },
    ...options,
  });
};

export { getLazyLoaderRenderFunction, lazyLoader };
